/**
 * Profile on the phone website (web-account-pages D8; shared-client-screens rows P1 to P3): the
 * app's own Profile screen (ProfileScreen) in the phone tab layout's frame, fed the website
 * session's profile and saved cards. Its legal links open in a new tab (P3). The website adds
 * Sign out, in its own card under Preferences (P2): the app has none, since Telegram signs the
 * client in each time. Signed out only once the server ended the session; then the studio's home.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useClientShellContext } from '@/components/client/ClientShell/ClientShellContext';
import { useToast } from '@/components/Toast/ToastContext';
import { ProfileScreen } from '@/pages/client/ProfileScreen';
import { ProfileActionCard } from '@/pages/client/ProfileView';

import { useSignOut } from '../api/session';
import { useWebPaymentMethods, useWebProfile, useWebRemoveCard, useWebUpdateProfile } from '../hooks/useWebAccount';

export function PhoneProfile() {
  const navigate = useNavigate();
  const { setHeaderSlot } = useClientShellContext();
  const { showToast } = useToast();
  const profile = useWebProfile();
  const cards = useWebPaymentMethods();
  const update = useWebUpdateProfile();
  const remove = useWebRemoveCard();
  const signOut = useSignOut();

  const onBack = useCallback(() => navigate('/'), [navigate]);
  const endSession = () => {
    void signOut().then((ended) => {
      if (ended) navigate('/');
      else showToast('Sign out did not go through. Please try again.', 'error');
    });
  };

  return (
    <ProfileScreen
      onBack={onBack}
      setHeaderSlot={setHeaderSlot}
      profile={{
        status: profile.isPending ? 'loading' : profile.isError ? 'error' : 'ready',
        data: profile.data,
        retry: () => void profile.refetch(),
      }}
      cards={{ status: cards.isPending ? 'loading' : cards.isError ? 'unavailable' : 'ready', list: cards.data?.cards ?? [] }}
      save={update.mutateAsync}
      saving={update.isPending}
      saveError={update.isError}
      resetSave={update.reset}
      removeCard={remove.mutateAsync}
      removing={remove.isPending}
      removeError={remove.isError}
      resetRemove={remove.reset}
      afterPreferences={<ProfileActionCard label="Sign out" onPress={endSession} />}
    />
  );
}
